import React, { createContext, useState, useContext } from 'react';

const DownloadsContext = createContext();

export function DownloadsProvider({ children }) {
  const [downloads, setDownloads] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [abortControllers, setAbortControllers] = useState(new Map());
  const [cancelHandlers, setCancelHandlers] = useState(new Map());

  const addDownload = (downloadId, fileName, gameTitle, platform, type, abortController = null, cancelHandler = null) => {
    setDownloads(prev => {
      // Check if download already exists
      if (prev.find(d => d.id === downloadId)) {
        return prev;
      }
      const newDownloads = [...prev, {
        id: downloadId,
        fileName,
        gameTitle,
        platform,
        type,
        progress: 0,
        status: 'downloading', // 'downloading', 'completed', 'error'
        error: null,
        exiting: false
      }];
      
      // Store abort controller if provided
      if (abortController) {
        setAbortControllers(prev => new Map(prev).set(downloadId, abortController));
      }
      
      // Store cancel handler if provided
      if (cancelHandler) {
        setCancelHandlers(prev => new Map(prev).set(downloadId, cancelHandler));
      }
      
      // Show component when first download is added
      if (newDownloads.length === 1) {
        setIsVisible(true);
      }
      
      return newDownloads;
    });
  };

  const updateDownloadProgress = (downloadId, progress) => {
    setDownloads(prev => 
      prev.map(d => d.id === downloadId ? { ...d, progress } : d)
    );
  };

  const updateCancelHandler = (downloadId, cancelHandler) => {
    if (cancelHandler) {
      setCancelHandlers(prev => new Map(prev).set(downloadId, cancelHandler));
    }
  };

  const completeDownload = (downloadId) => {
    setDownloads(prev => 
      prev.map(d => d.id === downloadId ? { ...d, status: 'completed', progress: 100 } : d)
    );
    // Mark as exiting and remove after 2 seconds (with animation)
    setTimeout(() => {
      setDownloads(prev => 
        prev.map(d => d.id === downloadId ? { ...d, exiting: true } : d)
      );
      // Wait for animation to complete before removing
      setTimeout(() => {
        removeDownload(downloadId);
      }, 300); // Animation duration
    }, 2000);
  };

  const failDownload = (downloadId, error) => {
    setDownloads(prev => 
      prev.map(d => d.id === downloadId ? { ...d, status: 'error', error } : d)
    );
    // Mark as exiting and remove after 5 seconds (with animation)
    setTimeout(() => {
      setDownloads(prev => 
        prev.map(d => d.id === downloadId ? { ...d, exiting: true } : d)
      );
      // Wait for animation to complete before removing
      setTimeout(() => {
        removeDownload(downloadId);
      }, 300); // Animation duration
    }, 5000);
  };

  const cancelDownload = (downloadId) => {
    // Abort the fetch request if it exists
    const controller = abortControllers.get(downloadId);
    if (controller) {
      controller.abort();
      setAbortControllers(prev => {
        const newMap = new Map(prev);
        newMap.delete(downloadId);
        return newMap;
      });
    }
    
    // Call custom cancel handler if provided (for cleanup like intervals)
    const cancelHandler = cancelHandlers.get(downloadId);
    if (cancelHandler) {
      cancelHandler();
      setCancelHandlers(prev => {
        const newMap = new Map(prev);
        newMap.delete(downloadId);
        return newMap;
      });
    }
    
    // Mark as exiting and remove
    setDownloads(prev => 
      prev.map(d => d.id === downloadId ? { ...d, exiting: true, status: 'cancelled' } : d)
    );
    
    setTimeout(() => {
      removeDownload(downloadId);
    }, 300); // Wait for animation
  };

  const removeDownload = (downloadId) => {
    // Clean up abort controller
    setAbortControllers(prev => {
      const newMap = new Map(prev);
      newMap.delete(downloadId);
      return newMap;
    });
    
    // Clean up cancel handler
    setCancelHandlers(prev => {
      const newMap = new Map(prev);
      newMap.delete(downloadId);
      return newMap;
    });
    
    setDownloads(prev => {
      const newDownloads = prev.filter(d => d.id !== downloadId);
      // Hide component when no downloads remain
      if (newDownloads.length === 0) {
        setIsVisible(false);
      }
      return newDownloads;
    });
  };

  const clearAllDownloads = () => {
    setDownloads([]);
    setIsVisible(false);
  };

  return (
    <DownloadsContext.Provider value={{
      downloads,
      isVisible,
      addDownload,
      updateDownloadProgress,
      completeDownload,
      failDownload,
      cancelDownload,
      updateCancelHandler,
      removeDownload,
      clearAllDownloads
    }}>
      {children}
    </DownloadsContext.Provider>
  );
}

export function useDownloads() {
  const context = useContext(DownloadsContext);
  if (!context) {
    throw new Error('useDownloads must be used within a DownloadsProvider');
  }
  return context;
}

